/**
 * Dedicated Cost Analysis Page — `/polygon/sites/:siteId/cost-analysis`
 *
 * All data from single pre-computed endpoint. Target: < 300ms page load.
 * Shows Capital vs O&M cost breakdown clearly.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../../components/animations/FadeIn';
import { RemediationSystemsPanel } from '../../components/polygon/RemediationSystemsPanel';
import {
  CostProjectionSnapshotResponse,
  CostProjectionMonthly,
  CostBuckets,
  fetchCostProjectionSnapshot,
  fetchCostBuckets,
  fetchV32CostCurve,
  type V32CostCurveResponse,
} from '../../api/polygon';
import { apiClient } from '../../api/client';

// Tier config (same as PolygonSiteModeling)
const TIER_CONFIG: Record<number, { label: string; style: string; bgStyle: string }> = {
  1: { label: 'CRITICAL', style: 'text-mono-100', bgStyle: 'bg-mono-300' },
  2: { label: 'HIGH', style: 'text-mono-200', bgStyle: 'bg-mono-400' },
  3: { label: 'MODERATE', style: 'text-mono-300', bgStyle: 'bg-mono-500' },
  4: { label: 'LOW', style: 'text-mono-400', bgStyle: 'bg-mono-600' },
  5: { label: 'MINIMAL', style: 'text-mono-500', bgStyle: 'bg-mono-700' },
};

// QC well patterns to filter from sentinel display (matches backend)
const QC_WELL_PATTERNS = ['Dup', 'Blank', 'Composite', 'Stockpile', 'Duplicate'];

function isRealWellName(name: string | null | undefined): boolean {
  if (!name) return true;
  return !QC_WELL_PATTERNS.some(p => name.includes(p));
}

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000).toLocaleString()}K`;
  return `$${value.toFixed(0)}`;
}

function formatCurrencyFull(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

function techLabel(tech: string): string {
  const labels: Record<string, string> = {
    mdpe: 'MDPE', solar_skimmer: 'Solar Skimmer', pump_and_treat: 'Pump & Treat',
    air_sparging: 'Air Sparging', sve: 'SVE', isco: 'ISCO',
    bioremediation: 'Bioremediation', mna: 'MNA',
  };
  return labels[tech] || tech.replace(/_/g, ' ');
}

interface SiteMeta {
  id: string;
  name: string;
  risk_score?: number;
}

/**
 * Derive annual O&M from a monthly projection snapshot.
 * Each projection's monitoring/regulatory/remediation costs are 5-year totals.
 */
function deriveAnnualOm(mp: CostProjectionMonthly | null) {
  if (!mp) return { monitoring: 0, regulatory: 0, remediation: 0, total: 0 };
  const monitoring = (mp.monitoring_cost || 0) / 5;
  const regulatory = (mp.regulatory_cost || 0) / 5;
  const remediation = (mp.base_remediation_cost || 0) / 5;
  return {
    monitoring,
    regulatory,
    remediation,
    total: monitoring + regulatory + remediation,
  };
}

export function CostAnalysisPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<CostProjectionSnapshotResponse | null>(null);
  const [siteMeta, setSiteMeta] = useState<SiteMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buckets, setBuckets] = useState<CostBuckets | null>(null);
  const [v32Cost, setV32Cost] = useState<V32CostCurveResponse | null>(null);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchV32CostCurve(siteId)
      .then(resp => { if (!cancelled) setV32Cost(resp); })
      .catch(err => {
        if (cancelled) return;
        console.warn('[POLYGON] v3.2 cost curve fetch failed:', err);
        setV32Cost(null);
      });
    return () => { cancelled = true; };
  }, [siteId]);

  const loadBuckets = useCallback(async (fresh = false) => {
    if (!siteId) return;
    try {
      const data = await fetchCostBuckets(siteId, fresh);
      setBuckets(data);
    } catch (err) {
      console.error('Cost buckets fetch failed:', err);
    }
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        // Fetch cost snapshot + site metadata in parallel
        // Cost buckets are embedded in the snapshot — no separate call needed
        const [snap, siteResp] = await Promise.all([
          fetchCostProjectionSnapshot(siteId!),
          apiClient.get(`/sites/${siteId}`).then(r => r.data).catch(() => null),
        ]);

        if (cancelled) return;
        setSnapshot(snap);
        // Extract embedded cost_buckets from snapshot (instant, no extra API call)
        const embedded = snap?.projection?.cost_buckets;
        if (embedded) {
          setBuckets(embedded);
        } else {
          // Snapshot exists but no embedded buckets — fetch separately
          const bucketsResp = await fetchCostBuckets(siteId!).catch(() => null);
          if (bucketsResp && !cancelled) setBuckets(bucketsResp);
        }
        if (siteResp) {
          setSiteMeta({
            id: siteResp.id || siteId!,
            name: siteResp.name || snap.site_name || 'Unknown Site',
            risk_score: siteResp.risk_score,
          });
        } else {
          setSiteMeta({
            id: siteId!,
            name: snap.site_name || 'Unknown Site',
          });
        }
      } catch (err) {
        console.error('Failed to load cost analysis:', err);
        if (!cancelled) setError('Failed to load cost analysis data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [siteId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-mono-500 text-sm tracking-wider">LOADING COST ANALYSIS...</p>
        </div>
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <ExclamationTriangleIcon className="h-12 w-12 text-mono-600 mb-4" />
        <h2 className="text-mono-200 text-lg tracking-wider mb-2">COST ANALYSIS UNAVAILABLE</h2>
        <p className="text-mono-500 text-sm mb-6">{error || 'Failed to load cost projection data.'}</p>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-wider transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          BACK
        </button>
      </div>
    );
  }

  const proj = snapshot.projection;
  const tier = proj?.threat_tier || 3;
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG[3];
  const siteName = siteMeta?.name || snapshot.site_name || 'Unknown Site';

  // Not yet pre-computed
  if (!snapshot.precomputed || !proj) {
    return (
      <div className="space-y-6">
        <FadeIn>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-mono-500 hover:text-mono-300 text-xs tracking-wider mb-4 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              BACK
            </button>
            <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">{siteName}</h1>
          </div>
        </FadeIn>
        <FadeIn delay={100}>
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-12 text-center">
            <ChartBarIcon className="h-16 w-16 text-mono-700 mx-auto mb-4" />
            <h2 className="text-mono-300 text-lg tracking-wider mb-2">COST PROJECTION NOT YET COMPUTED</h2>
            <p className="text-mono-500 text-sm max-w-md mx-auto">
              Cost projections are computed in the background when site data is uploaded or updated.
              Check back after uploading analytical results or documents.
            </p>
            <Link
              to={`/polygon/sites/${siteId}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-wider mt-6 transition-colors"
            >
              VIEW SITE DETAILS
            </Link>
          </div>
        </FadeIn>
      </div>
    );
  }

  const currentCost = snapshot.current_cost || 0;
  const cost6mo = snapshot.cost_6mo || 0;
  const cost12mo = snapshot.cost_12mo || 0;
  const cost24mo = snapshot.cost_24mo || 0;
  const burnRate = snapshot.monthly_burn_rate || 0;
  const monthlyProjections = proj.monthly_projections || [];
  const sentinelWells = (proj.sentinel_wells || []).filter(sw => isRealWellName(sw.trigger_well));
  const activityCosts = proj.activity_costs_used || {};
  const wellCount = snapshot.well_count || 0;

  // Derive cost structure from first monthly projection (act now scenario)
  // Each monthly projection's O&M costs represent 5-year totals
  const baselineProj = monthlyProjections.length > 0 ? monthlyProjections[0] : null;

  // Capital costs (one-time)
  const capitalWellInstall = baselineProj?.well_installation_cost || 0;
  const capitalPerimeter = baselineProj?.perimeter_well_cost || 0;
  const totalCapital = capitalWellInstall + capitalPerimeter;

  // Annual O&M (derived from 5-year totals)
  const annualOm = deriveAnnualOm(baselineProj);

  // Contingency
  const baselineContingency = baselineProj?.contingency || 0;

  // Cost of delay: difference between acting now vs waiting
  const costOfDelay6mo = cost6mo - currentCost;
  const costOfDelay12mo = cost12mo - currentCost;
  const costPerDayDelay = costOfDelay12mo > 0 ? costOfDelay12mo / 365 : 0;

  // Show quarterly rows in timeline (every 3 months) if many projections
  const timelineRows = monthlyProjections.length > 12
    ? monthlyProjections.filter((mp: CostProjectionMonthly) => mp.month % 3 === 0 || mp.month === 1)
    : monthlyProjections;

  // Activity costs grouped by category
  const wellInstallCosts = Object.entries(activityCosts).filter(([k]) => k.startsWith('mw_install'));
  const monitoringCosts = Object.entries(activityCosts).filter(([k]) =>
    ['gw_sampling', 'lab_analysis'].includes(k)
  );
  const reportingCosts = Object.entries(activityCosts).filter(([k]) =>
    ['quarterly_report', 'annual_report'].includes(k)
  );
  const fieldCosts = Object.entries(activityCosts).filter(([k]) =>
    ['mobilization', 'soil_boring', 'idw_disposal', 'well_abandonment', 'well_development', 'survey_staking', 'equipment_rental'].includes(k)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex items-start justify-between">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-mono-500 hover:text-mono-300 text-xs tracking-wider mb-4 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              BACK
            </button>
            <div className="flex items-center gap-4">
              <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full text-lg font-bold ${tierConfig.bgStyle} text-mono-950`}>
                {tier}
              </span>
              <div>
                <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">{siteName}</h1>
                <p className="text-mono-500 text-sm mt-1">
                  Cost Analysis | Computed {snapshot.computed_at ? new Date(snapshot.computed_at).toLocaleDateString() : 'recently'}
                  {snapshot.computation_ms ? ` (${snapshot.computation_ms}ms)` : ''}
                </p>
              </div>
            </div>
          </div>
          <div className="text-right space-y-2">
            <span className={`inline-block px-3 py-1 rounded text-xs tracking-wider ${tierConfig.bgStyle} text-mono-950`}>
              COST URGENCY {tier} - {tierConfig.label}
            </span>
            {siteMeta?.risk_score != null && (
              <div className="text-mono-500 text-xs">
                ARGOS Risk Score: <span className="text-mono-200 font-semibold">{siteMeta.risk_score}</span>/100
              </div>
            )}
          </div>
        </div>
      </FadeIn>

      {/* Alert for urgent tiers */}
      {tier <= 2 && (
        <FadeIn delay={50}>
          <div className="bg-mono-900 border border-mono-600 rounded-lg p-6">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-8 w-8 text-mono-300 mr-4 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-mono-100 tracking-wider font-orbitron">
                  {tier === 1 ? 'COST ESCALATION CRITICAL' : 'HIGH COST URGENCY'}
                </h2>
                <p className="text-mono-400 text-sm mt-1">
                  {tier === 1
                    ? `Costs are escalating rapidly at ${formatCurrency(burnRate)}/month. Immediate action recommended to prevent cost doubling.`
                    : `Monthly burn rate of ${formatCurrency(burnRate)} with significant 12-month cost increase projected.`}
                </p>
              </div>
            </div>
          </div>
        </FadeIn>
      )}

      {/* ====== Cost Buckets ====== */}
      {buckets && (
        <FadeIn delay={100}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* BUCKET 1: New Well Installation (Capital) */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
              <h4 className="text-xs font-semibold text-mono-300 tracking-wider mb-4">NEW WELL INSTALLATION</h4>
              <p className="text-mono-100 text-3xl font-bold font-orbitron">{formatCurrencyFull(buckets.new_well_installation.total)}</p>
              <p className="text-mono-200 text-sm tracking-wider mt-1">CAPITAL (ONE-TIME)</p>
              <div className="mt-4 pt-4 border-t border-mono-800 space-y-2">
                <div className="flex justify-between">
                  <span className="text-mono-200 text-sm">Recommended Wells</span>
                  <span className="text-mono-100 text-sm font-semibold">{sentinelWells.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-mono-200 text-sm">Cost per Well{buckets.new_well_installation.avg_depth_ft ? ` (avg. ${buckets.new_well_installation.avg_depth_ft} ft)` : ''}</span>
                  <span className="text-mono-100 text-sm font-semibold">{formatCurrencyFull(buckets.new_well_installation.cost_per_well)}</span>
                </div>
              </div>
            </div>

            {/* BUCKET 2: Recommended Remediation (from hotspot analysis) */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
              <h4 className="text-xs font-semibold text-mono-300 tracking-wider mb-4">RECOMMENDED REMEDIATION</h4>
              {buckets.recommended_remediation && buckets.recommended_remediation.hotspots.length > 0 ? (
                <>
                  <p className="text-mono-100 text-3xl font-bold font-orbitron">
                    {buckets.recommended_remediation.hotspots.length}
                    <span className="text-mono-300 text-lg font-normal"> hotspot{buckets.recommended_remediation.hotspots.length !== 1 ? 's' : ''}</span>
                  </p>
                  <p className="text-mono-200 text-sm tracking-wider mt-1">BASED ON SITE CONDITIONS</p>
                  <div className="mt-4 pt-4 border-t border-mono-800 space-y-2">
                    {buckets.recommended_remediation.hotspots.map(h => (
                      <div key={h.well_id} className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-mono-100 text-sm font-semibold">{h.well_name}</span>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {h.types.includes('psh') && (
                              <span className="px-1.5 py-0.5 rounded text-xs tracking-wider bg-amber-900/40 text-amber-400 border border-amber-800">PSH</span>
                            )}
                            {h.types.includes('dissolved') && (
                              <span className="px-1.5 py-0.5 rounded text-xs tracking-wider bg-cyan-900/40 text-cyan-400 border border-cyan-800">DISSOLVED</span>
                            )}
                            {v32Cost?.hotspot_overlay[h.well_id]?.remediate_now && (
                              <span
                                className="px-1.5 py-0.5 rounded text-xs tracking-wider bg-red-900/40 text-red-300 border border-red-700"
                                title={`v3.2 still exceeding at ${v32Cost.liability_horizon_label} · P=${(v32Cost.hotspot_overlay[h.well_id].worst_horizon_p_exceed * 100).toFixed(0)}%`}
                              >
                                {v32Cost.liability_horizon_label.toUpperCase()} EXCEEDS · REMEDIATE NOW
                              </span>
                            )}
                          </div>
                          {h.psh_thickness_ft != null && (
                            <p className="text-mono-200 text-xs mt-0.5">{h.psh_thickness_ft.toFixed(2)} ft product</p>
                          )}
                          {h.dissolved_compound && h.dissolved_max_conc_ugl != null && (
                            <p className="text-mono-200 text-xs mt-0.5">{h.dissolved_compound}: {(h.dissolved_max_conc_ugl / 1000).toFixed(3)} mg/L</p>
                          )}
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-[11px] tracking-wider shrink-0 ${
                          h.severity === 'critical' ? 'bg-red-900/40 text-red-400 border border-red-800'
                          : h.severity === 'high' ? 'bg-orange-900/40 text-orange-400 border border-orange-800'
                          : 'bg-mono-800 text-mono-400 border border-mono-700'
                        }`}>
                          {h.severity.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-mono-100 text-3xl font-bold font-orbitron">0</p>
                  <p className="text-mono-200 text-sm tracking-wider mt-1">NO HOTSPOTS DETECTED</p>
                  <div className="mt-4 pt-4 border-t border-mono-800">
                    <p className="text-mono-300 text-sm">No wells flagged for additional remediation based on current conditions.</p>
                  </div>
                </>
              )}
            </div>

            {/* BUCKET 3: O&M (Remediation + Monitoring merged) */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
              <h4 className="text-xs font-semibold text-mono-300 tracking-wider mb-4">O&M</h4>
              <p className="text-mono-100 text-3xl font-bold font-orbitron">
                {formatCurrencyFull(buckets.active_remediation.monthly_total + Math.round(buckets.om_monitoring.quarterly_total / 3))}
                <span className="text-mono-300 text-lg font-normal">/mo</span>
              </p>
              <p className="text-mono-100 text-sm tracking-wider mt-1">
                {formatCurrencyFull(buckets.active_remediation.annual_total + buckets.om_monitoring.annual_total)}/YR
              </p>

              {/* Active Remediation section */}
              <div className="mt-4 pt-4 border-t border-mono-800">
                <p className="text-mono-200 text-xs tracking-wider mb-2">ACTIVE REMEDIATION</p>
                {buckets.active_remediation.systems.length > 0 ? (
                  <div className="space-y-2">
                    {buckets.active_remediation.systems.map(sys => (
                      <div key={sys.system_id}>
                        <div className="flex justify-between">
                          <span className="text-mono-200 text-sm">{techLabel(sys.technology)}</span>
                          <span className="text-mono-100 text-sm font-semibold">{formatCurrencyFull(sys.monthly_cost)}/mo</span>
                        </div>
                        {/* MDPE breakdown */}
                        {sys.mdpe_breakdown && (
                          <div className="ml-3 mt-1 space-y-0.5">
                            <div className="flex justify-between">
                              <span className="text-mono-300 text-xs">
                                Equipment ({sys.mdpe_breakdown.equipment.events} x {formatCurrencyFull(sys.mdpe_breakdown.equipment.rate)} / {sys.mdpe_breakdown.equipment.duration})
                              </span>
                              <span className="text-mono-100 text-xs">{formatCurrencyFull(sys.mdpe_breakdown.equipment.total)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-mono-300 text-xs">Lab Analysis</span>
                              <span className="text-mono-100 text-xs">{formatCurrencyFull(sys.mdpe_breakdown.lab.monthly)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-mono-300 text-xs">
                                Tedlar ({sys.mdpe_breakdown.tedlar.per_event}/event x {formatCurrencyFull(sys.mdpe_breakdown.tedlar.rate)})
                              </span>
                              <span className="text-mono-100 text-xs">{formatCurrencyFull(sys.mdpe_breakdown.tedlar.total)}</span>
                            </div>
                            {sys.mdpe_breakdown.fuel.included && (
                              <div className="flex justify-between">
                                <span className="text-mono-300 text-xs">Fuel ({sys.mdpe_breakdown.equipment.events} visits)</span>
                                <span className="text-mono-100 text-xs">{formatCurrencyFull(sys.mdpe_breakdown.fuel.total)}</span>
                              </div>
                            )}
                            {sys.mdpe_breakdown.per_diem.included && (
                              <div className="flex justify-between">
                                <span className="text-mono-300 text-xs">Per Diem ({sys.mdpe_breakdown.equipment.events} days)</span>
                                <span className="text-mono-100 text-xs">{formatCurrencyFull(sys.mdpe_breakdown.per_diem.total)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-mono-300 text-sm">No active systems declared</p>
                )}
              </div>

              {/* Monitoring section */}
              <div className="mt-3 pt-3 border-t border-mono-800">
                <p className="text-mono-200 text-xs tracking-wider mb-2">MONITORING</p>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-mono-200 text-sm">Sampling ({buckets.om_monitoring.well_count} wells)</span>
                    <span className="text-mono-100 text-sm font-semibold">{formatCurrencyFull(buckets.om_monitoring.quarterly_sampling_cost)}/qtr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-mono-200 text-sm">Quarterly Report</span>
                    <span className="text-mono-100 text-sm font-semibold">{formatCurrencyFull(buckets.om_monitoring.quarterly_report_cost)}/qtr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-mono-200 text-sm">Mobilization</span>
                    <span className="text-mono-100 text-sm font-semibold">{formatCurrencyFull(buckets.om_monitoring.quarterly_mobilization)}/qtr</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      )}

      {/* ====== Annual Summary ====== */}
      {buckets && (
        <FadeIn delay={120}>
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="grid grid-cols-4 gap-6">
              <div>
                <p className="text-mono-300 text-xs tracking-wider mb-1">ANNUAL OPERATING</p>
                <p className="text-mono-100 text-2xl font-bold font-orbitron">{formatCurrency(buckets.grand_total_annual)}</p>
              </div>
              <div>
                <p className="text-mono-300 text-xs tracking-wider mb-1">CAPITAL BUDGET</p>
                <p className="text-mono-100 text-2xl font-bold font-orbitron">{formatCurrency(buckets.grand_total_capital)}</p>
              </div>
              <div>
                <p className="text-mono-300 text-xs tracking-wider mb-1">ACTIVE WELLS</p>
                <p className="text-mono-100 text-2xl font-bold font-orbitron">{wellCount}</p>
              </div>
              <div>
                <p className="text-mono-300 text-xs tracking-wider mb-1">MONTHLY BURN RATE</p>
                <p className="text-mono-100 text-2xl font-bold font-orbitron">{formatCurrency(burnRate)}</p>
              </div>
            </div>
          </div>
        </FadeIn>
      )}

      {/* ====== Remediation Systems Management ====== */}
      <FadeIn delay={140}>
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <RemediationSystemsPanel siteId={siteId!} onSystemsChanged={() => loadBuckets(true)} />
        </div>
      </FadeIn>

      {/* ====== Projection Timeline ====== */}
      <FadeIn delay={200}>
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">
              COST ESCALATION TIMELINE
            </h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-mono-500 text-xs">
                <ClockIcon className="h-4 w-4" />
                {monthlyProjections.length} month projection
              </div>
            </div>
          </div>

          <p className="text-mono-300 text-sm mb-4">
            Each row shows the total 5-year project cost if remediation begins at that month. Costs increase as plume grows.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-mono-800">
                  <th className="text-left text-mono-300 text-xs tracking-wider py-3 px-4">MONTH</th>
                  <th className="text-right text-mono-300 text-xs tracking-wider py-3 px-4">WELLS</th>
                  <th className="text-right text-mono-300 text-xs tracking-wider py-3 px-4">CAPITAL</th>
                  <th className="text-right text-mono-300 text-xs tracking-wider py-3 px-4">ANNUAL O&M</th>
                  <th className="text-right text-mono-300 text-xs tracking-wider py-3 px-4">5-YR TOTAL</th>
                  <th className="text-right text-mono-300 text-xs tracking-wider py-3 px-4">vs ACT NOW</th>
                </tr>
              </thead>
              <tbody>
                {timelineRows.map((mp: CostProjectionMonthly, idx: number) => {
                  const capital = (mp.well_installation_cost || 0) + (mp.perimeter_well_cost || 0);
                  const annual = deriveAnnualOm(mp);
                  const vsNow = (mp.cost_increase_from_baseline || 0);

                  return (
                    <tr key={idx} className="border-b border-mono-800 hover:bg-mono-850">
                      <td className="py-3 px-4">
                        <span className="text-mono-100 text-sm">{mp.month} mo</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <div className="flex flex-col items-end">
                          <span className="text-mono-100 text-sm">
                            {(mp.perimeter_wells_needed || 0) + wellCount}
                          </span>
                          {(mp.perimeter_wells_needed || 0) > 0 && (
                            <span className="text-mono-300 text-xs">
                              +{mp.perimeter_wells_needed} new
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="text-mono-100 text-sm">{formatCurrency(capital)}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="text-mono-100 text-sm">{formatCurrency(annual.total)}/yr</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="text-mono-100 font-semibold text-sm">{formatCurrency(mp.total_cost || 0)}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`text-sm ${vsNow > 0 ? 'text-mono-100' : 'text-mono-500'}`}>
                          {vsNow > 0 ? `+${formatCurrency(vsNow)}` : '--'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary row */}
          <div className="mt-6 pt-6 border-t border-mono-800 grid grid-cols-4 gap-6">
            <div>
              <p className="text-mono-300 text-xs tracking-wider mb-1">24-MONTH TOTAL</p>
              <p className="text-mono-100 text-2xl font-bold font-orbitron">{formatCurrency(cost24mo)}</p>
            </div>
            <div>
              <p className="text-mono-300 text-xs tracking-wider mb-1">MONTHLY BURN RATE</p>
              <p className="text-mono-100 text-2xl font-bold font-orbitron">{formatCurrency(burnRate)}</p>
            </div>
            <div>
              <p className="text-mono-300 text-xs tracking-wider mb-1">ACTIVE WELLS</p>
              <p className="text-mono-100 text-2xl font-bold font-orbitron">{wellCount}</p>
            </div>
            <div>
              <p className="text-mono-300 text-xs tracking-wider mb-1">COST DOUBLES AT</p>
              <p className="text-mono-100 text-2xl font-bold font-orbitron">
                {proj.cost_doubles_at_month != null ? `${proj.cost_doubles_at_month} mo` : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </FadeIn>

      {/* ====== Cost of Delay Analysis ====== */}
      <FadeIn delay={250}>
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron mb-6">
            COST OF DELAY ANALYSIS
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-mono-850 rounded-lg p-5">
              <p className="text-mono-300 text-xs tracking-wider mb-2">YEAR 1 COST</p>
              <p className="text-mono-100 text-3xl font-bold font-orbitron">{formatCurrency(currentCost)}</p>
              <p className="text-mono-200 text-sm mt-2">Operating + capital</p>
            </div>
            <div className="bg-mono-850 rounded-lg p-5">
              <p className="text-mono-300 text-xs tracking-wider mb-2">WAIT 6 MONTHS</p>
              <p className="text-mono-100 text-3xl font-bold font-orbitron">{formatCurrency(cost6mo)}</p>
              <p className="text-mono-200 text-sm mt-2">
                +{formatCurrency(costOfDelay6mo)} additional cost
              </p>
            </div>
            <div className="bg-mono-850 rounded-lg p-5">
              <p className="text-mono-300 text-xs tracking-wider mb-2">WAIT 12 MONTHS</p>
              <p className="text-mono-100 text-3xl font-bold font-orbitron">{formatCurrency(cost12mo)}</p>
              <p className="text-mono-100 text-sm mt-2">
                +{formatCurrency(costOfDelay12mo)} additional ({formatCurrency(costPerDayDelay)}/day)
              </p>
            </div>
          </div>
        </div>
      </FadeIn>

      {/* ====== Sentinel Well Recommendations ====== */}
      {sentinelWells.length > 0 && (
        <FadeIn delay={300}>
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron mb-6">
              SENTINEL WELL COST ESTIMATES
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-mono-800">
                    <th className="text-left text-mono-300 text-xs tracking-wider py-3 px-4">PRIORITY</th>
                    <th className="text-left text-mono-300 text-xs tracking-wider py-3 px-4">TRIGGER WELL</th>
                    <th className="text-left text-mono-300 text-xs tracking-wider py-3 px-4">RATIONALE</th>
                    <th className="text-center text-mono-300 text-xs tracking-wider py-3 px-4">EST. DEPTH</th>
                    <th className="text-right text-mono-300 text-xs tracking-wider py-3 px-4">INSTALL COST</th>
                  </tr>
                </thead>
                <tbody>
                  {sentinelWells.map((sw, idx) => (
                    <tr key={idx} className="border-b border-mono-800 hover:bg-mono-850">
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-xs tracking-wider ${
                          sw.priority === 'critical' ? 'bg-mono-300 text-mono-950' :
                          sw.priority === 'high' ? 'bg-mono-400 text-mono-950' :
                          'bg-mono-600 text-mono-200'
                        }`}>
                          {sw.priority.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-mono-100 text-sm">{sw.trigger_well || '--'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-mono-200 text-sm">{sw.rationale}</span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className="text-mono-200 text-sm">{(sw.avg_depth_ft || buckets?.new_well_installation?.avg_depth_ft) ? `${sw.avg_depth_ft || buckets.new_well_installation.avg_depth_ft} ft` : '--'}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="text-mono-100 text-sm font-semibold">{formatCurrency(sw.install_cost)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-mono-800 flex justify-between items-center">
              <span className="text-mono-300 text-sm">
                {sentinelWells.length} recommended well{sentinelWells.length !== 1 ? 's' : ''}
              </span>
              <span className="text-mono-100 text-sm font-semibold">
                Total: {formatCurrency(sentinelWells.reduce((sum, sw) => sum + sw.install_cost, 0))}
              </span>
            </div>
          </div>
        </FadeIn>
      )}

      {/* ====== Unit Cost Reference ====== */}
      <FadeIn delay={350}>
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">
              UNIT COST REFERENCE
            </h3>
            <span className="text-xs text-cyan-400 bg-cyan-900/30 px-2 py-0.5 rounded tracking-wider">
              FROM ACTIVITY UNIT COSTS
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Well Installation */}
            {wellInstallCosts.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-mono-300 tracking-wider mb-3">WELL INSTALLATION</h4>
                <div className="space-y-2">
                  {wellInstallCosts.map(([code, cost]) => (
                    <div key={code} className="flex justify-between">
                      <span className="text-mono-200 text-xs">{code === 'mw_install_shallow' ? 'Under 75 ft' : code === 'mw_install_mid' ? '75–150 ft' : code === 'mw_install_deep' ? 'Over 150 ft' : code.replace('mw_install_', '')}</span>
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(cost)}/well</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monitoring */}
            {monitoringCosts.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-mono-300 tracking-wider mb-3">MONITORING</h4>
                <div className="space-y-2">
                  {monitoringCosts.map(([code, cost]) => (
                    <div key={code} className="flex justify-between">
                      <span className="text-mono-200 text-xs">{code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(cost)}/event</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reporting */}
            {reportingCosts.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-mono-300 tracking-wider mb-3">REPORTING</h4>
                <div className="space-y-2">
                  {reportingCosts.map(([code, cost]) => (
                    <div key={code} className="flex justify-between">
                      <span className="text-mono-200 text-xs">{code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(cost)}/event</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Field Activities */}
            {fieldCosts.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-mono-300 tracking-wider mb-3">FIELD ACTIVITIES</h4>
                <div className="space-y-2">
                  {fieldCosts.map(([code, cost]) => (
                    <div key={code} className="flex justify-between">
                      <span className="text-mono-200 text-xs">{code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(cost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </FadeIn>

      {/* ====== Risk Cross-Reference ====== */}
      {siteMeta?.risk_score != null && (
        <FadeIn delay={400}>
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron mb-6">
              RISK vs COST URGENCY
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-mono-850 rounded-lg p-5">
                <p className="text-mono-300 text-xs tracking-wider mb-2">ARGOS RISK SCORE</p>
                <p className="text-mono-100 text-3xl font-bold font-orbitron">{siteMeta.risk_score}<span className="text-mono-300 text-lg">/100</span></p>
                <p className="text-mono-200 text-sm mt-2">XGBoost ensemble (physical + regulatory + financial + timeline)</p>
              </div>
              <div className="bg-mono-850 rounded-lg p-5">
                <p className="text-mono-300 text-xs tracking-wider mb-2">COST URGENCY</p>
                <p className={`text-3xl font-bold font-orbitron ${tierConfig.style}`}>
                  TIER {tier} - {tierConfig.label}
                </p>
                <p className="text-mono-200 text-sm mt-2">Based on 6-month cost escalation rate</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-mono-800">
              <p className="text-mono-200 text-sm">
                {(() => {
                  const highRisk = (siteMeta.risk_score || 0) >= 60;
                  const highCost = tier <= 2;
                  if (highRisk && highCost) return 'Critical on all fronts. Both environmental risk and cost escalation are elevated. Prioritize immediate remediation.';
                  if (highRisk && !highCost) return 'Environmentally serious but financially stable. Environmental risk is high, but costs are not escalating rapidly. Address environmental concerns without cost urgency pressure.';
                  if (!highRisk && highCost) return 'Contamination stable but costs escalating. Environmental risk is manageable, but delaying action increases costs significantly. Act to control cost growth.';
                  return 'Low priority. Both risk and cost urgency are low. Continue quarterly monitoring.';
                })()}
              </p>
            </div>
          </div>
        </FadeIn>
      )}

      {/* Footer links */}
      <FadeIn delay={450}>
        <div className="flex items-center gap-4">
          <Link
            to={`/polygon/sites/${siteId}`}
            className="flex items-center gap-2 px-4 py-2 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-wider transition-colors"
          >
            VIEW SITE DETAILS
          </Link>
          <Link
            to={`/polygon/sites/${siteId}/documents`}
            className="flex items-center gap-2 px-4 py-2 bg-mono-800 hover:bg-mono-700 text-mono-300 text-xs tracking-wider transition-colors"
          >
            DOCUMENT VAULT
          </Link>
        </div>
      </FadeIn>
    </div>
  );
}
